/**
 * PulseFilterPanel — the Pulse dashboard's full-overlay filter view (design:
 * gripp-os `FilterPanel`, packages/ui/src/components/filter-panel.tsx).
 *
 * Opened from the header FilterBadge, it covers the dashboard body (map + card
 * strip) with its own header row — a back chevron + "Filter (N)" title (N = the
 * active, non-"All" selection count) and a reset control on the right — over
 * three single-select pill sections (ACTIVITY TYPES, GROUPS, LABELS). Each
 * category defaults to its first option ("All"); selecting an option toggles
 * it, re-selecting the active one falls back to "All".
 *
 * Pure presentation: selection state is page-owned (PulseDashboard), so the
 * panel, the map and the card strip all read/write one source. Activity-type
 * pills carry a leading colour dot (the canonical Crop Circle severity/type
 * swatches); Groups/Labels are plain text pills populated from real workspace
 * data. Web-only — do NOT import from mobile code.
 */
import {
  ArrowLeftIcon,
  DotIcon,
  ResetIcon,
  tokens,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

export interface FilterOption {
  id: string;
  label: string;
  /** Leading colour dot (activity-type swatches). Omit for plain text pills. */
  dotColor?: string;
}

export interface FilterCategory {
  id: string;
  label: string;
  options: FilterOption[];
}

interface PulseFilterPanelProps {
  categories: FilterCategory[];
  /**
   * categoryId -> the selected option ids in that category (multi-select). An
   * empty / absent array means the category is on its "All" default. The first
   * option of each category is the "All" pill (selected iff the array is empty).
   */
  selections: Record<string, string[]>;
  /** Active (non-default) selection count — drives the header title number. */
  activeCount: number;
  onSelect: (categoryId: string, optionId: string) => void;
  onReset: () => void;
  onClose: () => void;
}

export const PulseFilterPanel = ({
  categories,
  selections,
  activeCount,
  onSelect,
  onReset,
  onClose,
}: PulseFilterPanelProps) => {
  const { t } = useTranslation();
  const [backHovered, setBackHovered] = useState(false);
  const [resetHovered, setResetHovered] = useState(false);

  return (
    <View style={styles.root} testID="pulse-dashboard-filter-panel">
      {/* Header row — mirrors the dashboard top bar (56px, white, bottom border).
          Left: back chevron + "Filter (N)". Right: reset. */}
      <View style={styles.header}>
        <Pressable
          style={styles.headerBack}
          onPress={onClose}
          onHoverIn={() => setBackHovered(true)}
          onHoverOut={() => setBackHovered(false)}
          accessibilityRole="button"
          accessibilityLabel={t('home.filters.close', 'Close filters')}
          testID="pulse-dashboard-filter-back"
        >
          <ArrowLeftIcon
            size={20}
            color={
              backHovered
                ? tokens.colors.neutral[600]
                : tokens.colors.neutral[800]
            }
          />
          <Text style={styles.headerTitle}>
            {t('home.filters.label', 'Filter')}
          </Text>
          <Text style={styles.headerCount}>({activeCount})</Text>
        </Pressable>

        <Pressable
          style={styles.headerReset}
          onPress={onReset}
          onHoverIn={() => setResetHovered(true)}
          onHoverOut={() => setResetHovered(false)}
          disabled={activeCount === 0}
          accessibilityRole="button"
          accessibilityLabel={t('home.filters.reset', 'Reset filters')}
          accessibilityState={{ disabled: activeCount === 0 }}
          testID="pulse-dashboard-filter-reset"
        >
          <ResetIcon
            size={20}
            color={
              activeCount === 0
                ? tokens.colors.slate[300]
                : resetHovered
                ? tokens.colors.neutral[600]
                : tokens.colors.neutral[800]
            }
          />
        </Pressable>
      </View>

      {/* Category sections — vertical stack of labelled pill rows. */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        showsVerticalScrollIndicator={false}
      >
        {categories.map((category) => (
          <View key={category.id} style={styles.section}>
            <Text style={styles.sectionLabel}>{category.label}</Text>
            <View style={styles.pillRow}>
              {category.options.map((option, index) => {
                const selected = selections[category.id] ?? [];
                // The first option is "All" — active when nothing else is.
                const isSelected =
                  index === 0
                    ? selected.length === 0
                    : selected.includes(option.id);
                return (
                  <FilterPill
                    key={option.id}
                    option={option}
                    selected={isSelected}
                    onPress={() => onSelect(category.id, option.id)}
                  />
                );
              })}
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

/**
 * FilterPill — a single selectable option. Selected = dark fill / white text;
 * unselected = soft neutral fill / dark text (hover lifts it). The "All" pill
 * has no dot and renders as a compact circle when selected (design: black
 * filled "All" chip), but otherwise shares the rounded-pill shape.
 */
const FilterPill = ({
  option,
  selected,
  onPress,
}: {
  option: FilterOption;
  selected: boolean;
  onPress: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      style={[
        styles.pill,
        selected ? styles.pillSelected : styles.pillUnselected,
        !selected && hovered && styles.pillHovered,
      ]}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={option.label}
      accessibilityState={{ selected }}
      testID={`pulse-filter-pill-${option.id}`}
    >
      {option.dotColor && <DotIcon size={8} color={option.dotColor} />}
      <Text
        style={[
          styles.pillText,
          selected ? styles.pillTextSelected : styles.pillTextUnselected,
        ]}
      >
        {option.label}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  root: {
    // Fills its half-width column in the dashboard split (see PulseDashboard).
    flex: 1,
    backgroundColor: tokens.colors.brand.white,
    borderLeftWidth: 1,
    borderLeftColor: tokens.colors.slate[100],
  },
  header: {
    height: tokens.spacing['7xl'], // 56px — matches the dashboard top bar
    paddingHorizontal: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.brand.white,
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.slate[100],
  },
  headerBack: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  headerTitle: {
    ...tokens.typography.title.small,
    fontWeight: '700',
    color: tokens.colors.neutral[800],
  },
  headerCount: {
    ...tokens.typography.title.small,
    color: tokens.colors.neutral[600],
  },
  headerReset: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing['2xl'],
  },
  section: {
    gap: tokens.spacing.md,
  },
  sectionLabel: {
    ...tokens.typography.label.large,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: tokens.colors.neutral[800],
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    height: 40,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
  },
  pillSelected: {
    backgroundColor: tokens.colors.neutral[800],
  },
  pillUnselected: {
    backgroundColor: tokens.colors.slate[100],
  },
  pillHovered: {
    backgroundColor: tokens.colors.slate[200],
  },
  pillText: {
    ...tokens.typography.body.medium,
  },
  pillTextSelected: {
    color: tokens.colors.brand.white,
  },
  pillTextUnselected: {
    color: tokens.colors.neutral[800],
  },
});
